import { CommonModule } from "@angular/common";
import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy
} from "@angular/core";
import { TrafficLightTimelineConfig } from "src/app/interfaces/entities/traffic-light";

@Component({
    selector: "app-timeline",
    templateUrl: "./timeline-component.html",
    styleUrls: ["./timeline-component.css"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule]
})
export class TimelineComponent implements OnInit, OnChanges {
  @Input() statusConfigs: TrafficLightTimelineConfig[] = [];
  @Input() configId?: string;
  timelineItems: Array<{
    type: "dot" | "line";
    color?: string;
    widthPercent?: number;
    days?: number | string;
  }> = [];

  days = $localize`:@@days:Days`;
  date = $localize`:@@date:Date`;

  ngOnInit(): void {
    this.updateTimeline();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update timeline whenever statusConfigs change
    if (changes["statusConfigs"]) {
      this.updateTimeline();
    }
  }

  updateTimeline(): void {
    // Ensure we have valid statusConfigs before processing
    if (!this.statusConfigs || this.statusConfigs.length === 0) {
      this.timelineItems = [];
      return;
    }

    // Sort by predefined color order instead of days
    const before = this.statusConfigs
      .filter((s) => s.before === true)
      .sort((a, b) => a.id - b.id);
    const after = this.statusConfigs
      .filter((s) => s.before === false)
      .sort((a, b) => a.id - b.id);
    const totalBefore = before.reduce((sum, s) => sum + s.days, 0);
    const totalAfter = after.reduce((sum, s) => sum + s.days, 0);
    const total = totalBefore + totalAfter || 1; // Avoid division by 0
    const timeline: Array<{
      type: "dot" | "line";
      color?: string;
      widthPercent?: number;
      days?: number | string;
    }> = [];
    let lineWidthSum = 0; // Track sum of line widths for normalization

    // If all before → move dot to right
    if (before.length > 0 && after.length === 0) {
      before.forEach((s, i) => {
        timeline.push({
          type: "dot",
          color: s.color,
          days: s.days,
        });
        const nextDays = i < before.length - 1 ? before[i + 1].days : 0;
        const width = ((s.days - nextDays) / total) * 100;
        lineWidthSum += width;
        timeline.push({
          type: "line",
          color: s.color,
          widthPercent: width,
        });
      });
      timeline.push({ type: "dot", color: "#ebebebff", days: "Date" });
    }
    // If all after → dot at beginning
    else if (after.length > 0 && before.length === 0) {
      timeline.push({ type: "dot", color: "#ebebebff", days: "Date" });
      after.forEach((s, i) => {
        const prevDays = i > 0 ? after[i - 1].days : 0;
        const width = ((s.days - prevDays) / total) * 100;
        lineWidthSum += width;
        timeline.push({
          type: "line",
          color: s.color,
          widthPercent: width,
        });
        timeline.push({
          type: "dot",
          color: s.color,
          days: s.days,
        });
      });
    }
    // Mixed case → dot in middle
    else {
      before.forEach((s, i) => {
        timeline.push({
          type: "dot",
          color: s.color,
          days: s.days,
        });
        const nextDays = i < before.length - 1 ? before[i + 1].days : 0;
        const width = ((s.days - nextDays) / total) * 100;
        lineWidthSum += width;
        timeline.push({
          type: "line",
          color: s.color,
          widthPercent: width,
        });
      });
      timeline.push({
        type: "dot",
        color: "#ebebebff",
        days: $localize`:@@days:Days`,
      }); // central date dot
      after.forEach((s, i) => {
        const prevItem = timeline[timeline.length - 1];
        const prevDotColor =
          prevItem.type === "dot"
            ? prevItem.color
            : before.length > 0
              ? before[before.length - 1].color
              : s.color;
        const prevDays = i > 0 ? after[i - 1].days : 0;
        const width = ((s.days - prevDays) / total) * 100;
        lineWidthSum += width;
        timeline.push({
          type: "line",
          color:
            i === 0 && before.length > 0
              ? before[before.length - 1].color
              : prevDotColor,
          widthPercent: width,
        });
        timeline.push({
          type: "dot",
          color: s.color,
          days: s.days,
        });
      });
      // Add small line after the final Red dot
      if (after.length > 0) {
        const width = 10; // Fixed 10% for final line
        lineWidthSum += width;
        timeline.push({
          type: "line",
          color: after[after.length - 1].color,
          widthPercent: width,
        });
      }
    }

    // Normalize line widths to ensure they sum to 100%
    if (lineWidthSum > 0) {
      const scaleFactor = 100 / lineWidthSum;
      timeline.forEach((item) => {
        if (item.type === "line" && item.widthPercent) {
          item.widthPercent = item.widthPercent * scaleFactor;
        }
      });
    }

    this.timelineItems = timeline;
  }
}
